import type { ReactNode } from 'react';
import { cx } from '../cx';

export type HeroProps = {
  /** A live line ("4 177 jobs today"). Drop the count, not the line, when the snapshot lacks it. */
  eyebrow?: ReactNode;
  /** Shows the pulsing live dot before the eyebrow. */
  live?: boolean;
  /** The headline; wrap one phrase in <Highlight>. */
  title: ReactNode;
  lead?: ReactNode;
  /** The SearchField: the page's one primary action. */
  search?: ReactNode;
  /** Small print under the search (popular searches). */
  below?: ReactNode;
  /** The coverage panel beside the text on wide screens. */
  aside?: ReactNode;
  /** h1 on the landing page; h2 only where the page already has its h1 (the showcase). */
  as?: 'h1' | 'h2';
  /** Centres the text column below lg (the lead, search and small print included). */
  centered?: boolean;
  /** Width of the aside from lg up: narrow = 2 of 5 columns (default), wide = half the width. */
  asideWidth?: 'narrow' | 'wide';
  /**
   * default: the hero sits on the page. forest: on a full-bleed deep-forest band (edge to edge, behind the
   * whole hero) with on-forest text; the search and the aside keep their own white surfaces. The page
   * wrapper needs `overflow-x: clip` so the 100vw band cannot cause a horizontal scroll.
   */
  tone?: 'default' | 'forest';
  /** Decorative art drawn on the forest band, inside the hero's width and clipped to the band (aria-hidden, no pointer events). */
  backdrop?: ReactNode;
  className?: string;
};

/** A solid sun underlay behind one phrase of a headline (never a gradient). */
export type HighlightProps = { children: ReactNode };

export function Highlight({ children }: HighlightProps) {
  return <span className="rounded-jc-info bg-jc-cta box-decoration-clone px-1.5 text-jc-on-cta">{children}</span>;
}

/** The landing hero: live eyebrow, headline, lead, search, and a panel showing who is hiring. */
export function Hero({
  eyebrow,
  live = false,
  title,
  lead,
  search,
  below,
  aside,
  as: Heading = 'h1',
  centered = false,
  asideWidth = 'narrow',
  tone = 'default',
  backdrop,
  className,
}: HeroProps) {
  const forest = tone === 'forest';
  return (
    <section
      className={cx(
        'grid items-center gap-10 py-10 md:py-16',
        forest && 'relative isolate',
        aside && asideWidth === 'wide' ? 'lg:grid-cols-2' : 'lg:grid-cols-5',
        className,
      )}
    >
      <div
        className={cx(
          'flex flex-col gap-5',
          centered && 'items-center text-center lg:items-start lg:text-left',
          aside ? (asideWidth === 'wide' ? '' : 'lg:col-span-3') : 'lg:col-span-5 lg:max-w-3xl',
        )}
      >
        {eyebrow ? (
          <span
            className={cx(
              'inline-flex items-center gap-2 text-sm font-semibold',
              forest ? 'text-jc-on-forest-muted [--jc-live:var(--jc-on-forest-muted)]' : 'text-jc-ink-secondary',
            )}
          >
            {live ? (
              <span aria-hidden="true" className="relative flex size-2.5">
                <span className="absolute inline-flex size-full animate-ping rounded-full bg-jc-live opacity-60 motion-reduce:animate-none" />
                <span className="relative inline-flex size-2.5 rounded-full bg-jc-live" />
              </span>
            ) : null}
            {eyebrow}
          </span>
        ) : null}
        <Heading
          className={cx(
            'type-hero',
            forest ? 'text-jc-on-forest [&_a]:underline [&_a]:underline-offset-4' : 'text-jc-ink',
          )}
        >
          {title}
        </Heading>
        {lead ? (
          <p className={cx('max-w-xl text-base', forest ? 'text-jc-on-forest-muted' : 'text-jc-ink-secondary')}>
            {lead}
          </p>
        ) : null}
        {search ? <div className="w-full max-w-2xl">{search}</div> : null}
        {below ? <div className="text-sm text-jc-ink-secondary">{below}</div> : null}
      </div>
      {aside ? <div className={cx('min-w-0', asideWidth === 'wide' ? '' : 'lg:col-span-2')}>{aside}</div> : null}
      {/* Last child on purpose: the text column stays :first-of-type and the aside :nth-of-type(2) for page CSS. */}
      {forest ? (
        <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 -bottom-8 -z-10">
          <div className="absolute inset-y-0 left-1/2 w-screen -translate-x-1/2 bg-jc-forest-deep" />
          {backdrop ? <div className="absolute inset-0 overflow-hidden">{backdrop}</div> : null}
        </div>
      ) : null}
    </section>
  );
}
